import {
    ApiQuery,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { TokenTimeseriesColumns } from "~/modules/token_timeseries/types";
import { LeagueTableGroupTypes } from "~/modules/league_tables/types";
import { LeagueTableChartConfig } from "~/modules/charts/types";
import { MEASURE_SLUGS } from "~/consts";

// // // //

export const LEAGUE_TABLE_MAX_ROWS_OPTIONS = [10, 25, 50, 100];

export const DEFAULT_LEAGUE_TABLE_MAX_ROWS = 10;

// Default to bridged token value dollar if measure_slug is not set (edge-case)
export const DEFAULT_LEAGUE_TABLE_MEASURE_SLUG: string =
    MEASURE_SLUGS.bridged_token_value_dollar;

/**
 * DEFAULT_LEAGUE_TABLE_QUERY
 * A token aggregates query with no conditions. The league table API only reads
 * `filter` - the aggregate, sort, and pagination are controlled server-side
 * per group tab - but `sort` and `pagination` are required on `ApiQuery`.
 */
export const DEFAULT_LEAGUE_TABLE_QUERY: ApiQuery = {
    filter: {
        operator: FilterOperators.And,
        filters: [],
    },
    sort: {
        direction: SortDirections.asc,
        field: TokenTimeseriesColumns.date,
    },
    pagination: {
        page: 1,
        perPage: 25,
    },
};

/**
 * DEFAULT_LEAGUE_TABLE_GROUPS
 * The group tabs a new league table starts with, in display order.
 */
export const DEFAULT_LEAGUE_TABLE_GROUPS: LeagueTableGroupTypes[] = [
    LeagueTableGroupTypes.networks,
    LeagueTableGroupTypes.platforms,
    LeagueTableGroupTypes.assetClasses,
];

export const DEFAULT_LEAGUE_TABLE_CONFIG: LeagueTableChartConfig = {
    measure_slug: DEFAULT_LEAGUE_TABLE_MEASURE_SLUG,
    query: DEFAULT_LEAGUE_TABLE_QUERY,
    groups: DEFAULT_LEAGUE_TABLE_GROUPS,
    max_rows: DEFAULT_LEAGUE_TABLE_MAX_ROWS,
};

// // // //

/**
 * getLeagueTableMeasureSlug
 * Reads the separately-stored measure_slug from a league table config.
 */
export function getLeagueTableMeasureSlug(
    config: LeagueTableChartConfig,
): string {
    const value = config.measure_slug;

    if (typeof value === "string" && value.length > 0) {
        return value;
    }

    return DEFAULT_LEAGUE_TABLE_MEASURE_SLUG;
}

/**
 * getLeagueTableGroups
 * Reads the group tabs from a league table config, in display order.
 */
export function getLeagueTableGroups(
    config: LeagueTableChartConfig,
): LeagueTableGroupTypes[] {
    return Array.isArray(config.groups) ? config.groups : [];
}

/**
 * getLeagueTableMaxRows
 * Reads the per-group row limit from a league table config.
 */
export function getLeagueTableMaxRows(config: LeagueTableChartConfig): number {
    const value = config.max_rows;

    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        return value;
    }

    return DEFAULT_LEAGUE_TABLE_MAX_ROWS;
}
